'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  Activity,
  BookOpen,
  Box as BoxIcon,
  Building2,
  Camera,
  ClipboardList,
  Cpu,
  FileText,
  Inbox,
  Layers,
  ListChecks,
  PackageSearch,
  QrCode,
  ScanLine,
  ScanSearch,
  ShieldCheck,
  Truck,
  Users as UsersIcon,
  Warehouse
} from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { clientQueries } from '@/features/clients';
import { deviceQueries } from '@/features/devices';
import { handoverScanningQueries } from '@/features/handover-scanning';
import { stickingQueries } from '@/features/sticking';
import { ocrQaQueries } from '@/features/ocr-qa';
import { packingQueries } from '@/features/packing';
import { photoScanningQueries } from '@/features/photo-scanning';
import { qrReviewQueries } from '@/features/qr-review';
import { receptionQueries } from '@/features/reception';
import { registrationQueries } from '@/features/registration';
import { reviewQueries } from '@/features/review';
import { shelfQueries } from '@/features/shelves';
import { shippingQueries } from '@/features/shipping';
import { sortingQueries } from '@/features/sorting';
import { storageLocationQueries } from '@/features/storage-locations';
import { userQueries } from '@/features/users';
import { canLoadMetric } from '../utils/metric-access';
import { DashboardSection } from './dashboard-section';
import type { StatCardData } from '../types';

export function DashboardOverview() {
  const t = useTranslations('dashboard');
  const { user } = useAuth();

  // One flag per metric, each mirroring its endpoint's own backend guard —
  // never the section it is rendered in. See `metric-access.ts`.
  const canRegistrations = canLoadMetric(user, 'registrations');
  const canBoxes = canLoadMetric(user, 'boxes');
  const canShipments = canLoadMetric(user, 'shipments');
  const canUsers = canLoadMetric(user, 'users');
  const canIndexing = canLoadMetric(user, 'indexing');
  const canReview = canLoadMetric(user, 'review');
  const canReception = canLoadMetric(user, 'reception');
  const canSorting = canLoadMetric(user, 'sorting');
  const canHandover = canLoadMetric(user, 'handover');
  const canPhotoScanning = canLoadMetric(user, 'photoScanning');
  const canQrReview = canLoadMetric(user, 'qrReview');
  const canQa1 = canLoadMetric(user, 'ocrQa1');
  const canQa2 = canLoadMetric(user, 'ocrQa2');
  const canQa3 = canLoadMetric(user, 'ocrQa3');
  const canShelves = canLoadMetric(user, 'shelves');
  const canStorageLocations = canLoadMetric(user, 'storageLocations');
  const canDevices = canLoadMetric(user, 'devices');
  const canClients = canLoadMetric(user, 'clients');

  const registrationsQ = useQuery({
    ...registrationQueries.metrics(),
    enabled: canRegistrations
  });
  const boxesQ = useQuery({
    ...packingQueries.metrics(),
    enabled: canBoxes
  });
  const shipmentsQ = useQuery({
    ...shippingQueries.metrics(),
    enabled: canShipments
  });
  const usersQ = useQuery({
    ...userQueries.metrics(),
    enabled: canUsers
  });
  const indexingQ = useQuery({
    ...stickingQueries.globalMetrics(),
    enabled: canIndexing
  });
  const reviewQ = useQuery({
    ...reviewQueries.metrics(),
    enabled: canReview
  });
  const receptionQ = useQuery({
    ...receptionQueries.metrics(),
    enabled: canReception
  });
  const sortingQ = useQuery({
    ...sortingQueries.metrics(),
    enabled: canSorting
  });
  const handoverQ = useQuery({
    ...handoverScanningQueries.metrics(),
    enabled: canHandover
  });
  const photoScanningQ = useQuery({
    ...photoScanningQueries.metrics(),
    enabled: canPhotoScanning
  });
  const qrReviewQ = useQuery({
    ...qrReviewQueries.metrics(),
    enabled: canQrReview
  });
  const ocrQa1Q = useQuery({
    ...ocrQaQueries.metrics(1),
    enabled: canQa1
  });
  const ocrQa2Q = useQuery({
    ...ocrQaQueries.metrics(2),
    enabled: canQa2
  });
  const ocrQa3Q = useQuery({
    ...ocrQaQueries.metrics(3),
    enabled: canQa3
  });
  const shelvesQ = useQuery({
    ...shelfQueries.stats(),
    enabled: canShelves
  });
  const storageLocationsQ = useQuery({
    ...storageLocationQueries.stats(),
    enabled: canStorageLocations
  });
  const devicesQ = useQuery({
    ...deviceQueries.metrics(),
    enabled: canDevices
  });
  const clientsQ = useQuery({
    ...clientQueries.metrics(),
    enabled: canClients
  });

  const registrations = registrationsQ.data?.data;
  const boxes = boxesQ.data?.data;
  const shipments = shipmentsQ.data?.data;
  const users = usersQ.data?.data;
  const indexing = indexingQ.data?.data;
  const review = reviewQ.data?.data;
  const reception = receptionQ.data?.data;
  const sorting = sortingQ.data?.data;
  const handover = handoverQ.data?.data;
  const photoScanning = photoScanningQ.data?.data;
  const qrReview = qrReviewQ.data?.data;
  const ocrQa1 = ocrQa1Q.data?.data;
  const ocrQa2 = ocrQa2Q.data?.data;
  const ocrQa3 = ocrQa3Q.data?.data;
  const shelves = shelvesQ.data?.data;
  const storageLocations = storageLocationsQ.data?.data;
  const devices = devicesQ.data?.data;
  const clients = clientsQ.data?.data;

  const glanceCards = useMemo<StatCardData[]>(() => {
    const cards: StatCardData[] = [];
    if (canRegistrations) {
      cards.push({
        label: t('cards.totalRegistrations'),
        value: registrations?.total ?? 0,
        icon: FileText,
        tone: 'blue',
        hint: t('hints.registrationsTotal')
      });
    }
    if (canBoxes) {
      cards.push({
        label: t('cards.totalBoxes'),
        value: boxes?.total ?? 0,
        icon: BoxIcon,
        tone: 'amber',
        hint: t('hints.boxesAcrossPipeline')
      });
    }
    if (canShipments) {
      cards.push({
        label: t('cards.totalShipments'),
        value: shipments?.total_shipments ?? 0,
        icon: Truck,
        tone: 'cyan',
        hint: t('hints.shipmentsLifetime')
      });
    }
    if (canUsers) {
      cards.push({
        label: t('cards.activeUsers'),
        value: users?.active_users ?? 0,
        icon: UsersIcon,
        tone: 'green',
        hint: t('hints.activeUsersLogin')
      });
    }
    return cards;
  }, [
    canRegistrations,
    canBoxes,
    canShipments,
    canUsers,
    registrations,
    boxes,
    shipments,
    users,
    t
  ]);

  const externalLabCards = useMemo<StatCardData[]>(() => {
    const cards: StatCardData[] = [];
    if (canRegistrations) {
      cards.push({
        label: t('cards.activeRegistrations'),
        value: registrations?.active ?? 0,
        icon: Activity,
        tone: 'indigo'
      });
    }
    if (canIndexing) {
      cards.push(
        {
          label: t('cards.containersInIndexing'),
          value: indexing?.total_containers_in_indexing ?? 0,
          icon: BookOpen,
          tone: 'purple',
          hint: t('hints.containersInProgress')
        },
        {
          label: t('cards.pagesPrintedToday'),
          value: indexing?.today_printed ?? 0,
          icon: ListChecks,
          tone: 'orange'
        }
      );
    }
    if (canReview) {
      cards.push({
        label: t('cards.pendingReview'),
        value: review?.pending_count ?? 0,
        icon: ClipboardList,
        tone: 'rose',
        hint: t('hints.pendingReviewCount')
      });
    }
    return cards;
  }, [
    canRegistrations,
    canIndexing,
    canReview,
    registrations,
    indexing,
    review,
    t
  ]);

  const internalLabCards = useMemo<StatCardData[]>(() => {
    const cards: StatCardData[] = [];
    if (canReception) {
      cards.push({
        label: t('cards.receptionProcessing'),
        value: reception?.processing ?? 0,
        icon: Inbox,
        tone: 'blue',
        hint: t('hints.receptionInProgress')
      });
    }
    if (canSorting) {
      cards.push({
        label: t('cards.sortingPending'),
        value: sorting?.pending ?? 0,
        icon: PackageSearch,
        tone: 'amber',
        hint: t('hints.sortingAwaiting')
      });
    }
    if (canHandover) {
      cards.push({
        label: t('cards.activeWorkloads'),
        value: handover?.total_active_workloads ?? 0,
        icon: ScanSearch,
        tone: 'indigo',
        hint: t('hints.scanAssignmentActive')
      });
    }
    if (canPhotoScanning) {
      cards.push({
        label: t('cards.scanBatches'),
        value: photoScanning?.total ?? 0,
        icon: Camera,
        tone: 'purple',
        hint: t('hints.scanUploadBatches')
      });
    }
    if (canQrReview) {
      cards.push({
        label: t('cards.qrReviewPending'),
        value: qrReview?.pending_batches ?? 0,
        icon: QrCode,
        tone: 'rose',
        hint: t('hints.qaReviewPending')
      });
    }
    return cards;
  }, [
    canReception,
    canSorting,
    canHandover,
    canPhotoScanning,
    canQrReview,
    reception,
    sorting,
    handover,
    photoScanning,
    qrReview,
    t
  ]);

  const ocrQaCards = useMemo<StatCardData[]>(() => {
    const cards: StatCardData[] = [];
    if (canQa1) {
      cards.push({
        label: t('cards.ocrQa1Pending'),
        value: ocrQa1?.pending ?? 0,
        icon: ScanLine,
        tone: 'blue',
        hint: t('hints.ocrTier', { tier: 1 })
      });
    }
    if (canQa2) {
      cards.push({
        label: t('cards.ocrQa2Pending'),
        value: ocrQa2?.pending ?? 0,
        icon: ScanLine,
        tone: 'indigo',
        hint: t('hints.ocrTier', { tier: 2 })
      });
    }
    if (canQa3) {
      cards.push({
        label: t('cards.ocrQa3Pending'),
        value: ocrQa3?.pending ?? 0,
        icon: ScanLine,
        tone: 'purple',
        hint: t('hints.ocrTier', { tier: 3 })
      });
    }
    return cards;
  }, [canQa1, canQa2, canQa3, ocrQa1, ocrQa2, ocrQa3, t]);

  const storageCards = useMemo<StatCardData[]>(() => {
    const cards: StatCardData[] = [];
    if (canShelves) {
      const utilization =
        typeof shelves?.avg_utilization === 'number'
          ? `${Math.round(shelves.avg_utilization)}%`
          : '0%';
      cards.push({
        label: t('cards.activeShelves'),
        value: shelves?.active_shelves ?? 0,
        icon: Layers,
        tone: 'amber',
        hint: t('hints.avgUtilization', { value: utilization })
      });
    }
    if (canStorageLocations) {
      cards.push({
        label: t('cards.warehouses'),
        value: storageLocations?.total_warehouses ?? 0,
        icon: Warehouse,
        tone: 'orange'
      });
    }
    if (canDevices) {
      cards.push({
        label: t('cards.devicesOnline'),
        value: devices?.online_count ?? 0,
        icon: Cpu,
        tone: 'green',
        hint: t('hints.outOfTotal', {
          total: devices?.total_devices ?? 0
        })
      });
    }
    if (canClients) {
      cards.push({
        label: t('cards.activeClients'),
        value: clients?.active_count ?? 0,
        icon: Building2,
        tone: 'cyan',
        hint: t('hints.clientsActive')
      });
    }
    return cards;
  }, [
    canShelves,
    canStorageLocations,
    canDevices,
    canClients,
    shelves,
    storageLocations,
    devices,
    clients,
    t
  ]);

  // A disabled query never loads, so each flag counts only the metrics this
  // role can actually fetch — otherwise a section skeleton would spin forever
  // on an endpoint the role was never allowed to call.
  const glanceLoading =
    (canRegistrations && registrationsQ.isLoading) ||
    (canBoxes && boxesQ.isLoading) ||
    (canShipments && shipmentsQ.isLoading) ||
    (canUsers && usersQ.isLoading);

  const externalLoading =
    (canRegistrations && registrationsQ.isLoading) ||
    (canIndexing && indexingQ.isLoading) ||
    (canReview && reviewQ.isLoading);

  const internalLoading =
    (canReception && receptionQ.isLoading) ||
    (canSorting && sortingQ.isLoading) ||
    (canHandover && handoverQ.isLoading) ||
    (canPhotoScanning && photoScanningQ.isLoading) ||
    (canQrReview && qrReviewQ.isLoading);

  const ocrQaLoading =
    (canQa1 && ocrQa1Q.isLoading) ||
    (canQa2 && ocrQa2Q.isLoading) ||
    (canQa3 && ocrQa3Q.isLoading);

  const storageLoading =
    (canShelves && shelvesQ.isLoading) ||
    (canStorageLocations && storageLocationsQ.isLoading) ||
    (canDevices && devicesQ.isLoading) ||
    (canClients && clientsQ.isLoading);

  const nothingVisible =
    glanceCards.length === 0 &&
    externalLabCards.length === 0 &&
    internalLabCards.length === 0 &&
    ocrQaCards.length === 0 &&
    storageCards.length === 0;

  if (nothingVisible) {
    return (
      <div className='flex min-h-[40vh] flex-col items-center justify-center gap-2 text-center'>
        <ShieldCheck className='text-muted-foreground h-10 w-10' />
        <p className='text-muted-foreground text-sm'>{t('emptyPermissions')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6 p-4 md:p-6'>
      <header className='space-y-1'>
        <h1 className='text-2xl font-bold tracking-tight md:text-3xl'>
          {t('title')}
        </h1>
        <p className='text-muted-foreground text-sm'>{t('subtitle')}</p>
      </header>

      <DashboardSection
        title={t('sections.atAGlance')}
        description={t('sections.atAGlanceDescription')}
        cards={glanceCards}
        isLoading={glanceLoading}
        skeletonCount={glanceCards.length}
      />

      <DashboardSection
        title={t('sections.externalLab')}
        description={t('sections.externalLabDescription')}
        cards={externalLabCards}
        isLoading={externalLoading}
        skeletonCount={externalLabCards.length}
      />

      <DashboardSection
        title={t('sections.internalLab')}
        description={t('sections.internalLabDescription')}
        cards={internalLabCards}
        isLoading={internalLoading}
        skeletonCount={internalLabCards.length}
        columnsClassName='grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
      />

      <DashboardSection
        title={t('sections.ocrQa')}
        description={t('sections.ocrQaDescription')}
        cards={ocrQaCards}
        isLoading={ocrQaLoading}
        skeletonCount={ocrQaCards.length}
        columnsClassName='grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      />

      <DashboardSection
        title={t('sections.storage')}
        description={t('sections.storageDescription')}
        cards={storageCards}
        isLoading={storageLoading}
        skeletonCount={storageCards.length}
      />
    </div>
  );
}
